import {useField, useFormikContext} from "formik"
import React, {useCallback, useEffect, useRef, useState} from "react"
import { TComponentTextarea, TField } from "./_types"

const Field = React.memo<TField>((props) => {
    const {
        article,
        resolvedClassName,
        value,
        rows,
        handleChange,
        handleBlur,
        handleOnKeyDown,
        is_disabled,
        isError,
        error,
        onIndicatorClick,
    } = props

    return (
        <>
            <div className='componentTextarea_wrapper'>
                <textarea
                    name={article}
                    value={value ?? ''}
                    className={resolvedClassName}
                    rows={rows}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={is_disabled}
                    onKeyDown={handleOnKeyDown}
                />
                <button
                    type='button'
                    className='componentTextarea_cornerIndicator'
                    onClick={onIndicatorClick}
                    aria-label='Настройки ввода Enter'
                >
                    <i className='bi bi-gear componentTextarea_cornerIconInner' aria-hidden='true' />
                </button>
            </div>
            {isError ? <div className='invalid_feedback'>{error}</div> : null}
        </>
    )
})

type TEnterMode = 'enter' | 'ctrlEnter'

const STORAGE_KEY = 'textareaEnterMode'

const getEnterModeLabel = (mode: TEnterMode) => {
    if (mode === 'ctrlEnter') return 'Ctrl + Enter'
    return 'Enter'
}

const ComponentTextarea: React.FC<TComponentTextarea> = ({ article, is_disabled, rows = 4, submitOnEnter, customHandler }) => {
    const [field, { error, touched }] = useField(article)
    const { handleSubmit } = useFormikContext()
    const { onChange, onBlur, value } = field
    const isError = Boolean(error && touched)

    const [enterMode, setEnterMode] = useState<TEnterMode>('enter')
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isShortcutDropdownOpen, setIsShortcutDropdownOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY) as TEnterMode | null
            if (stored === 'enter' || stored === 'ctrlEnter') {
                setEnterMode(stored)
            }
        } catch {
            // ignore
        }
    }, [])

    const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        return customHandler ? customHandler(event) : onChange(event)
    }, [customHandler, onChange])

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (enterMode === 'enter') {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                handleSubmit()
            }
        } else {
            if (event.key === 'Enter' && event.ctrlKey) {
                event.preventDefault()
                handleSubmit()
            }
        }
    }, [enterMode, handleSubmit])

    const toggleMenu = useCallback(() => {
        setIsMenuOpen((prev) => !prev)
        setIsShortcutDropdownOpen(false)
    }, [])

    const selectMode = useCallback((mode: TEnterMode) => {
        setEnterMode(mode)
        setIsShortcutDropdownOpen(false)
        setIsMenuOpen(false)
        try {
            window.localStorage.setItem(STORAGE_KEY, mode)
        } catch {
            // ignore
        }
    }, [])

    useEffect(() => {
        if (!isMenuOpen && !isShortcutDropdownOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node | null
            if (!target || !containerRef.current) return
            if (containerRef.current.contains(target)) return
            setIsMenuOpen(false)
            setIsShortcutDropdownOpen(false)
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMenuOpen(false)
                setIsShortcutDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isMenuOpen, isShortcutDropdownOpen])

    const resolvedClassName = `componentTextarea form-control form-control-solid${isError ? " invalid" : ""}`
    return (
        <div className='componentTextarea_container' ref={containerRef}>
            <Field
                resolvedClassName={resolvedClassName}
                article={article}
                value={value}
                rows={rows}
                handleChange={handleChange}
                handleBlur={onBlur}
                isError={isError}
                is_disabled={is_disabled}
                error={error}
                handleOnKeyDown={handleKeyDown}
                onIndicatorClick={toggleMenu}
            />
            {isMenuOpen && (
                <div className='componentTextarea_menu'>
                    <div className='componentTextarea_menuHeader'>Горячие клавиши</div>
                    <div className='componentTextarea_menuRow'>
                        <div className='componentTextarea_menuRowLabel'>Отправить сообщение</div>
                        <div className='componentTextarea_menuSelectWrap'>
                            <button
                                type='button'
                                className='componentTextarea_menuSelect'
                                onClick={() => setIsShortcutDropdownOpen((prev) => !prev)}
                            >
                                <span>{getEnterModeLabel(enterMode)}</span>
                                <i className='bi bi-chevron-down' />
                            </button>
                            {isShortcutDropdownOpen && (
                                <div className='componentTextarea_menuDropdown'>
                                    <button
                                        type='button'
                                        className={`componentTextarea_menuOption${enterMode === 'enter' ? ' active' : ''}`}
                                        onClick={() => selectMode('enter')}
                                    >
                                        <div className='componentTextarea_menuOptionText'>
                                            <div className='componentTextarea_menuOptionTitle'>Enter</div>
                                            <div className='componentTextarea_menuOptionHint'>
                                                Shift + Enter — перенос строки
                                            </div>
                                        </div>
                                        {enterMode === 'enter' && (
                                            <i className='bi bi-check componentTextarea_menuOptionCheck' />
                                        )}
                                    </button>
                                    <button
                                        type='button'
                                        className={`componentTextarea_menuOption${enterMode === 'ctrlEnter' ? ' active' : ''}`}
                                        onClick={() => selectMode('ctrlEnter')}
                                    >
                                        <div className='componentTextarea_menuOptionText'>
                                            <div className='componentTextarea_menuOptionTitle'>Ctrl + Enter</div>
                                            <div className='componentTextarea_menuOptionHint'>
                                                Enter — перенос строки
                                            </div>
                                        </div>
                                        {enterMode === 'ctrlEnter' && (
                                            <i className='bi bi-check componentTextarea_menuOptionCheck' />
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ComponentTextarea